import { useState } from "react";
import { media } from "@/config/site";
import { waMessages } from "@/lib/whatsapp";
import MediaImage from "@/components/feature/MediaImage";
import WhatsAppLink from "@/components/feature/WhatsAppLink";
import Lightbox, { type LightboxImage } from "@/components/feature/Lightbox";
import Reveal from "@/components/feature/Reveal";

const galleryImages: LightboxImage[] = [
  { src: media.gallery[0], alt: "Koyu renk uzun dalgalı saç şekillendirme çalışması" },
  { src: media.gallery[1], alt: "Balyaj saç renklendirme çalışması" },
  { src: media.gallery[2], alt: "Kıvırcık perma saç çalışması" },
  { src: media.gallery[3], alt: "Bakır tonlu renkli saç çalışması" },
  { src: media.gallery[4], alt: "Duvak ve kristal aksesuarlı zarif gelin topuzu" },
  { src: media.gallery[5], alt: "Işıltılı hacimli gelin topuzu" },
  { src: media.gallery[6], alt: "Çiçek aksesuarlı gelin topuzu" },
  { src: media.gallery[7], alt: "Helezon özel gün topuzu çalışması" },
];

const videos = [
  {
    key: "hair",
    title: "Saç Dönüşümü",
    poster: media.videoHair.poster,
    src: media.videoHair.src,
    label: "Saç dönüşümü hakkında sor",
    message: waMessages.hairVideo,
  },
  {
    key: "g5",
    title: "G5 Bakımı",
    poster: media.videoG5.poster,
    src: media.videoG5.src,
    label: "G5 bakımı hakkında sor",
    message: waMessages.g5Video,
  },
];

export default function Works() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return (
    <section id="calismalarimiz" className="scroll-mt-24 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] text-accent-600 md:text-sm">
            Gerçek Çalışmalar
          </p>
          <h2 className="mt-4 max-w-xl font-heading text-3xl leading-tight text-primary-800 md:text-5xl">
            Salonumuzdan seçili görünümler.
          </h2>
        </Reveal>

        <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5">
          {galleryImages.map((img, i) => (
            <Reveal key={i} delay={(i % 3) * 70}>
              <button
                onClick={() => setLightboxIndex(i)}
                className="group relative block w-full overflow-hidden rounded-lg bg-background-200"
                aria-label={`${img.alt} görselini büyüt`}
              >
                <div className="aspect-[3/4] w-full">
                  <MediaImage
                    src={img.src}
                    alt={img.alt}
                    className="h-full w-full transition-transform duration-500 group-hover:scale-[1.05]"
                  />
                </div>
                <span className="absolute inset-0 flex items-center justify-center bg-primary-950/0 opacity-0 transition-opacity duration-300 group-hover:bg-primary-950/30 group-hover:opacity-100">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-50/90 text-primary-800">
                    <i className="ri-zoom-in-line text-xl" aria-hidden="true"></i>
                  </span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {videos.map((video, i) => (
            <Reveal key={video.key} delay={i * 100}>
              <div className="overflow-hidden rounded-lg bg-background-50">
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-primary-950">
                  {video.src ? (
                    <video
                      src={video.src}
                      poster={video.poster}
                      controls
                      preload="none"
                      className="h-full w-full object-contain"
                    >
                      Tarayıcınız video oynatmayı desteklemiyor.
                    </video>
                  ) : (
                    <>
                      <img
                        src={video.poster}
                        alt={`${video.title} videosu önizlemesi`}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span
                          className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-950/50 text-background-50"
                          aria-hidden="true"
                        >
                          <i className="ri-play-line text-3xl"></i>
                        </span>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="font-heading text-2xl text-primary-800">{video.title}</h3>
                    <p className="mt-1 text-sm text-foreground-600">
                      Uygulama videosu — oynatmak için görsele tıklayın.
                    </p>
                  </div>
                  <WhatsAppLink
                    message={video.message}
                    label={video.label}
                    variant="outline"
                    size="sm"
                    sourceSection="works_video"
                    className="whitespace-nowrap"
                  />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={galleryImages}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </section>
  );
}